import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { reassignOrganization } from '../../utils/api/assistantAPI';
import { toast } from 'sonner';

/**
 * ReassignOrganizationModal
 * This modal allows users to select and reassign an assistant to a different organization.
 */
const ReassignOrganizationModal = ({ closeModal, handleCancelReassign, organizations = [] }) => {
  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Redux state for active assistant
  const activeDetail = useSelector(state => state?.assistants?.activeDetail?.assistant);
  const [activeAssistantID, setActiveAssistantID] = useState(null);

  // Set active assistant ID from redux only once
  useEffect(() => {
    if (activeDetail?.id) {
      setActiveAssistantID(activeDetail.id);
    }
  }, [activeDetail]);

  // Filtered organizations based on search input
  const filteredOrganizations = organizations.filter(org =>
    org?.name?.toLowerCase().includes(searchTerm?.toLowerCase())
  );

  // Close dropdown when clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * Handle selecting an organization from dropdown
   */
  const handleSelectOrganization = (org) => {
    setSelectedOrg(org);
    setSearchTerm(org.name);
    setIsDropdownOpen(false);
  };

  /**
   * Handle input changes in the search field
   */
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsDropdownOpen(true);

    // Clear selection if it doesn't match
    if (selectedOrg && !selectedOrg.name.toLowerCase().includes(value.toLowerCase())) {
      setSelectedOrg(null);
    }
  };

  /**
   * Handle the confirm button click to reassign
   */
  const handleConfirm = async () => {
    if (!selectedOrg || !activeAssistantID) return;

    try {
      await reassignOrganization(activeAssistantID, selectedOrg.id);
      
      closeModal();
      window.location.reload(); // Reload to reflect changes
    } catch (error) {
      console.error('Reassign Error:', error);
      toast.error('Failed to reassign organization. Please try again.');
    }
  };

  /**
   * Open dropdown on input focus/click
   */
  const handleSearchInputClick = () => {
    setIsDropdownOpen(true);
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1050,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          padding: '24px',
          width: '90%',
          maxWidth: '450px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}
      >
        <h5 style={{ marginBottom: '16px', fontWeight: '600' }}>
          Reassign Organization
        </h5>

        <p style={{ marginBottom: '20px', color: '#666' }}>
          Select a new organization to reassign this assistant to:
        </p>

        {/* Search Input & Dropdown */}
        <div style={{ position: 'relative', marginBottom: '24px' }} ref={dropdownRef}>
          <label
            htmlFor="orgSearch"
            style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}
          >
            Organization
          </label>

          <input
            id="orgSearch"
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            onClick={handleSearchInputClick}
            placeholder="Search organizations..."
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              outline: 'none',
              cursor: 'pointer'
            }}
          />

          {/* Dropdown List */}
          {isDropdownOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: '#fff',
                border: '1px solid #ddd',
                borderTop: 'none',
                borderRadius: '0 0 4px 4px',
                maxHeight: '200px',
                overflowY: 'auto',
                zIndex: 1000,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            >
              {filteredOrganizations.length > 0 ? (
                filteredOrganizations.map((org) => (
                  <div
                    key={org.id}
                    onClick={() => handleSelectOrganization(org)}
                    style={{
                      padding: '10px 12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f0f0f0',
                      backgroundColor: selectedOrg?.id === org.id ? '#f1f5f9' : '#fff',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f1f5f9';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = selectedOrg?.id === org.id ? '#f1f5f9' : '#fff';
                    }}
                  >
                    <div style={{ fontWeight: '500', fontSize: '14px' }}>{org.name}</div>
                    {org.description && (
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                        {org.description}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div
                  style={{
                    padding: '10px 12px',
                    color: '#999',
                    fontSize: '14px',
                    textAlign: 'center',
                  }}
                >
                  No organizations found
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="d-flex justify-content-end gap-2">
          <button className="btn btn-secondary" onClick={handleCancelReassign}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleConfirm}
            disabled={!selectedOrg}
            style={{
              opacity: selectedOrg ? 1 : 0.6,
              cursor: selectedOrg ? 'pointer' : 'not-allowed',
            }}
          >
            Reassign
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReassignOrganizationModal;
